import { supabase } from "@/lib/supabase";
import { handleError, ErrorSeverity, ErrorContext } from "@/utils/errorHandler";

export interface InvoiceStatusService {
  calculateInvoiceStatus: (invoiceId: string) => Promise<'paid' | 'partial' | 'unpaid'>;
  updateInvoiceStatus: (invoiceId: string, status: 'paid' | 'partial' | 'unpaid' | 'overdue') => Promise<boolean>;
  validatePaymentAmount: (invoiceId: string, paymentAmount: number) => Promise<{ valid: boolean; message?: string; remainingBalance?: number }>;
}

/**
 * Shared invoice status management service
 * Used by both manual payments and Stripe webhook handlers
 */
export const InvoiceStatusService: InvoiceStatusService = {
  /**
   * Calculate the correct invoice status based on payments received
   */
  calculateInvoiceStatus: async (invoiceId: string): Promise<'paid' | 'partial' | 'unpaid'> => {
    const context: ErrorContext = {
      operation: "calculateInvoiceStatus",
      additionalInfo: { invoiceId },
    };

    try {
      // Get invoice total and all successful payments
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_cents')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('amount_cents')
        .eq('invoice_id', invoiceId)
        .in('status', ['succeeded', 'completed']); // Handle both Stripe and manual payment statuses

      if (paymentsError) throw paymentsError;

      const totalPaid = payments?.reduce((sum, payment) => sum + payment.amount_cents, 0) || 0;
      const invoiceTotal = invoice.total_cents;

      if (totalPaid >= invoiceTotal) {
        return 'paid';
      } else if (totalPaid > 0) {
        return 'partial';
      } else {
        return 'unpaid';
      }
    } catch (error) {
      handleError(error, "Failed to calculate invoice status", ErrorSeverity.ERROR, context);
      // Default to current status if calculation fails
      return 'unpaid';
    }
  },

  /**
   * Update invoice status when a payment is applied
   */
  updateInvoiceStatus: async (invoiceId: string, status: 'paid' | 'partial' | 'unpaid' | 'overdue'): Promise<boolean> => {
    const context: ErrorContext = {
      operation: "updateInvoiceStatus",
      additionalInfo: { invoiceId, status },
    };

    try {
      const { error } = await supabase
        .from('invoices')
        .update({ status })
        .eq('id', invoiceId);

      if (error) throw error;
      return true;
    } catch (error) {
      handleError(error, "Failed to update invoice status", ErrorSeverity.WARNING, context);
      return false;
    }
  },

  /**
   * Validate payment amount against invoice (if linked)
   */
  validatePaymentAmount: async (invoiceId: string, paymentAmount: number): Promise<{ valid: boolean; message?: string; remainingBalance?: number }> => {
    try {
      // Get invoice total and existing payments
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('total_cents')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('amount_cents')
        .eq('invoice_id', invoiceId)
        .in('status', ['succeeded', 'completed']);

      if (paymentsError) throw paymentsError;

      const totalPaid = payments?.reduce((sum, payment) => sum + payment.amount_cents, 0) || 0;
      const remainingBalance = invoice.total_cents - totalPaid;

      if (remainingBalance <= 0) {
        return { 
          valid: false, 
          message: "Invoice is already fully paid",
          remainingBalance: 0
        };
      }

      if (paymentAmount > remainingBalance) {
        return { 
          valid: false, 
          message: `Payment amount ($${(paymentAmount / 100).toFixed(2)}) exceeds remaining balance ($${(remainingBalance / 100).toFixed(2)})`,
          remainingBalance
        };
      }

      if (paymentAmount <= 0) {
        return { 
          valid: false, 
          message: "Payment amount must be greater than zero",
          remainingBalance
        };
      }

      return { valid: true, remainingBalance };
    } catch (error) {
      return { 
        valid: false, 
        message: "Unable to validate payment amount",
        remainingBalance: 0
      };
    }
  }
};